import { faArrowDown, faArrowUp, faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Chip, Switch, Tooltip, CircularProgress } from '@material-ui/core';
import { getDifficultyColors } from 'components/reports/cells/ItemDifficulty';
import { ColumnDef, getCoreRowModel, RowSelectionState, useReactTable } from '@tanstack/react-table';
import welcomeImage from 'assets/images/hello.svg';
import LoadingComponent from 'components/LoadingComponent';
import { ArticleIcon } from 'components/TableFrame';
import { MoreVertButton } from 'components/buttons/MoreVertButton';
import { PrimaryButton } from 'components/buttons/v4';
import ImageBadge from 'components/images/ImageBadge';
import { BuildingStatusIndicator } from 'components/common/BuildingStatusIndicator';
import { ContentTableFilters, ContentTableFilterChips } from 'components/content/ContentTableFilters';
import { BulkActionBar, SelectionCount, DeselectButton, createSelectionColumn } from 'components/tables/BulkSelectionElements';
import { DeleteConfirmationModal } from 'components/modals/DeleteConfirmationModal';
import { ContentUsageModal } from 'components/modals/ContentUsageModal';
import { AddToCourseModal } from 'components/modals/AddToCourseModal';
import { LoadingModal } from 'components/modals/LoadingModal';
import { Pagination } from 'components/reports/controls/Pagination';
import { TableBody } from 'components/tables/learner-stats/TableBody';
import { TableHeader } from 'components/tables/learner-stats/TableHeader';
import { SortArrow, TableHeaderContainer, TableHeaderText } from 'components/tables/learner-stats/elements';
import { TableSearchBar } from 'components/tables/TableSearchBar';
import { Color } from 'core';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { useGetSets, ContentFilters } from 'hooks/useGetSets';
import useMutateSet from 'hooks/useMutateSet';
import { bulkDeleteSets } from 'hooks/useDeleteSet';
import { PRIVACY_TYPES } from 'models/assignment';
import moment from 'moment';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { formatStringForSortComparison } from 'utils/tableUtils';
import { getEditSetUrl, getEditSetUrlForReactRouter, getPreviewSetUrl, getPreviewSetUrlForReactRouter } from 'utils/pathUtils';
import { pluralize } from 'utils/stringUtils';
import { AnalyticsService } from 'utils/AnalyticsService';
import { useCopyContent } from 'hooks/useCopyContent';

interface ContentTableProps {
  registerSortReset?: (resetSort: () => void) => void;
}

export const ContentTable = (props: ContentTableProps = {}) => {
  const { registerSortReset } = props;
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [optimisticSharedStates, setOptimisticSharedStates] = useState<{ [key: string]: boolean }>({});
  const [showActionsMenu, setShowActionsMenu] = useState<{ [key: string]: boolean }>({});
  const [setsToDelete, setSetsToDelete] = useState<any[]>([]);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [showUsedInModal, setShowUsedInModal] = useState<boolean>(false);
  const [selectedSetForUsedIn, setSelectedSetForUsedIn] = useState<any>(null);
  const [showAddToCourseModal, setShowAddToCourseModal] = useState<boolean>(false);
  const [selectedSetForAddToCourse, setSelectedSetForAddToCourse] = useState<any>(null);
  const [excludedSurveyCountForModal, setExcludedSurveyCountForModal] = useState<number>(0);
  const [isBulkDeleteAction, setIsBulkDeleteAction] = useState<boolean>(false);

  // Row selection state for bulk actions
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  // Pagination state
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(25);

  // Search state
  const [searchName, setSearchName] = useState<string>('');

  // Filter state
  const [filters, setFilters] = useState<ContentFilters>({});

  const { t } = useTranslation();
  const user = useCurrentUser();
  const primaryPartnerId = user?.primaryPartner.id;

  const resetSort = () => {
    setSortColumn('');
    setSortDirection('asc');
    setPageNumber(1);
  };

  useEffect(() => {
    if (registerSortReset) {
      registerSortReset(resetSort);
    }
  }, [registerSortReset]);

  // Map frontend column names to API field names and create sort parameter
  const getSortParam = () => {
    if (!sortColumn) return '-updated_at'; // Default sort

    const sortMapping: { [key: string]: string } = {
      title: 'name',
      last_updated: 'updated_at',
      type: 'goal_type',
      difficulty: 'difficulty',
      used_in: 'courses_count',
    };

    const apiSortColumn = sortMapping[sortColumn] || sortColumn;
    return sortDirection === 'desc' ? `-${apiSortColumn}` : apiSortColumn;
  };

  const {
    data,
    status: queryStatus,
    isFetching,
    refetch,
  } = useGetSets(primaryPartnerId, true, pageNumber, pageSize, getSortParam(), searchName, filters);
  const sets = data?.data || [];
  const setsMeta = data?.meta;
  // Pagination calculations
  const totalCount = setsMeta ? setsMeta['total-count'] : 0;
  const totalPages = setsMeta ? setsMeta['total-pages'] : 0;

  // Check if any sets are building
  const hasBuildingSets = sets.some((set) => set.state === 'building');

  // Set up polling for building sets
  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (hasBuildingSets) {
      intervalId = setInterval(() => {
        refetch();
      }, 10000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [hasBuildingSets, refetch]);

  // Pagination handlers
  const goToPageOne = () => setPageNumber(1);
  const goToNextPage = () => pageNumber < totalPages && setPageNumber(pageNumber + 1);
  const goToPreviousPage = () => pageNumber > 1 && setPageNumber(pageNumber - 1);
  const goToLastPage = () => setPageNumber(totalPages);

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPageNumber(1);
  };

  // Reset page and clear selection when search or filters change
  useEffect(() => {
    setPageNumber(1);
    setRowSelection({});
  }, [searchName, filters]);

  // Clear selection when page changes
  useEffect(() => {
    setRowSelection({});
  }, [pageNumber, pageSize]);

  const [mutateSet] = useMutateSet();

  const { canCopy, canCopyAsAssessment, canCopyAsLearningSet, handleCopy, copyingSetId, isCopying } = useCopyContent({
    redirectOnSuccess: true,
  });

  const toggleSortDirection = () => {
    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
  };

  const getTypeDisplay = (goalType: string) => {
    const typeMap: { [key: string]: string } = {
      set: t('Learning Set'),
      survey: t('Survey'),
      assessment: t('Assessment'),
      scorm: t('SCORM'),
    };
    return typeMap[goalType] || goalType;
  };

  const getTypePillColor = (goalType: string) => {
    const colorMap: { [key: string]: { background: string; color: string } } = {
      set: { background: '#e3f2fd', color: '#1976d2' }, // Blue
      survey: { background: '#f3e5f5', color: '#7b1fa2' }, // Purple
      assessment: { background: '#e8f5e8', color: '#388e3c' }, // Green
      scorm: { background: '#fce4ec', color: '#c2185b' }, // Reddish
    };
    return colorMap[goalType] || { background: Color.veryLightGray, color: Color.textGray };
  };

  const getCourseCountText = (courses: any[]) => {
    const count = courses?.length || 0;
    if (count === 0) return 'No courses';
    return pluralize(count, 'course');
  };

  const getDeleteConfirmationBody = (setsToDelete: any[]): string => {
    const count = setsToDelete.length;
    const totalCourses = setsToDelete.reduce((sum, set) => sum + (set.courses?.length || 0), 0);
    const pronoun = count === 1 ? 'It is' : 'They are';
    const courseUsageText =
      totalCourses === 0
        ? `${pronoun} not used in any courses.`
        : `${pronoun} used in ${pluralize(totalCourses, 'course assignment')}.`;
    return `${pluralize(count, 'assignment')} will be permanently deleted. ${courseUsageText}`;
  };

  const getSharedState = (set: any): boolean => {
    return optimisticSharedStates.hasOwnProperty(set.id) ? optimisticSharedStates[set.id] : set.isShared || false;
  };

  const toggleSetPrivacy = (set: any): void => {
    if (!set) return;
    const currentSharedState = getSharedState(set);
    const newSharedState = !currentSharedState;

    // Optimistically update the UI
    setOptimisticSharedStates((prev) => ({
      ...prev,
      [set.id]: newSharedState,
    }));

    const privacyTypeId = newSharedState ? PRIVACY_TYPES.COURSES_ONLY : PRIVACY_TYPES.PRIVATE_ONLY;
    mutateSet({
      id: set.id,
      payload: { data: { attributes: { privacy_type_id: privacyTypeId } } },
    });
  };

  const toggleActionsMenu = (setId: string): void => {
    setShowActionsMenu((prev) => ({
      ...prev,
      [setId]: !prev[setId],
    }));
  };

  const handleDeleteClick = (set: any): void => {
    setIsBulkDeleteAction(false);
    setSetsToDelete([set]);
    // Close the actions menu
    setShowActionsMenu((prev) => ({
      ...prev,
      [set.id]: false,
    }));
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    if (setsToDelete.length === 0) return;

    if (isBulkDeleteAction) {
      AnalyticsService.getInstance().track('bulk_action_executed', {
        action_type: 'delete',
        item_count: setsToDelete.length,
      });
    }

    setIsDeleting(true);
    try {
      await bulkDeleteSets(setsToDelete.map((set) => set.id));
      // Clear row selection if bulk deleting
      if (setsToDelete.length > 1) {
        setRowSelection({});
      }
      setSetsToDelete([]);
    } catch (error) {
      console.error('Failed to delete set(s):', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = (): void => {
    setSetsToDelete([]);
  };

  const handleUsedInClick = (set: any): void => {
    setSelectedSetForUsedIn(set);
    setShowUsedInModal(true);
  };

  const handleUsedInModalClose = (): void => {
    setShowUsedInModal(false);
    setSelectedSetForUsedIn(null);
  };

  const handleAddToCourseClick = (set: any): void => {
    setSelectedSetForAddToCourse([set]); // Wrap in array for consistent modal interface
    setExcludedSurveyCountForModal(0); // Single item, no surveys excluded
    setShowAddToCourseModal(true);
    // Close the actions menu
    setShowActionsMenu((prev) => ({
      ...prev,
      [set.id]: false,
    }));
  };

  const handleAddToCourseModalClose = (didAddContent: boolean): void => {
    setShowAddToCourseModal(false);
    setSelectedSetForAddToCourse(null);
    // Only refetch if content was added AND the "no courses" filter is active
    // (items would disappear from view after being added to a course)
    const hasNoCoursesFilter = filters.usedIn?.includes('no_courses');
    if (didAddContent && hasNoCoursesFilter) {
      setRowSelection({}); // Clear selection before refetch to prevent stale selection counts
      refetch();
    }
  };

  // Bulk action handlers
  const selectedCount = Object.keys(rowSelection).length;
  const selectedSets = sets.filter((set) => rowSelection[set.id]);
  // Filter out surveys for Add to Course (surveys must be added from course detail page)
  const selectedSetsForCourse = selectedSets.filter((set) => set.goalType !== 'survey');
  const excludedSurveyCount = selectedSets.length - selectedSetsForCourse.length;
  const hasOnlySurveys = selectedSets.length > 0 && selectedSetsForCourse.length === 0;

  const handleBulkAddToCourse = (): void => {
    if (selectedSetsForCourse.length > 0) {
      AnalyticsService.getInstance().track('bulk_action_executed', {
        action_type: 'add_to_course',
        item_count: selectedSetsForCourse.length,
      });
      setSelectedSetForAddToCourse(selectedSetsForCourse);
      setExcludedSurveyCountForModal(excludedSurveyCount);
      setShowAddToCourseModal(true);
    }
  };

  const handleBulkDelete = (): void => {
    setIsBulkDeleteAction(true);
    setSetsToDelete(selectedSets);
  };

  const noSetsMessage = (
    <div style={{ color: Color.darkGray, textAlign: 'center' }}>
      <ArticleIcon src={welcomeImage} style={{ marginTop: '3em' }} />
      <h2>No Content Yet</h2>
      <p>Get started creating your first content.</p>
    </div>
  );

  const errorMessage = (
    <div style={{ color: Color.red, textAlign: 'center' }}>
      <h2>Error Loading Content</h2>
      <p>There was a problem loading your content. Please try refreshing the page.</p>
    </div>
  );

  const columns: ColumnDef<any>[] = [
    createSelectionColumn(),
    {
      id: 'title',
      accessorFn: (row) => row.name,
      cell: ({ row }) => {
        const rowData = row.original;
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <SContentImageContainer style={{ flex: 1 }}>
              <ImageBadge image={rowData.image} style={contentImageStyles} entity="set" />
            </SContentImageContainer>
            <div style={{ flex: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
              {rowData.state === 'building' && <BuildingStatusIndicator />}
              {rowData.goalType !== 'scorm' && rowData.state !== 'building' ? (
                rowData.learnVersion === 4 ? (
                  <TitleLink to={getEditSetUrlForReactRouter(rowData.learnVersion, rowData.id)}>{rowData.name}</TitleLink>
                ) : (
                  <TitleAnchor href={getEditSetUrl(rowData.learnVersion, rowData.id)}>{rowData.name}</TitleAnchor>
                )
              ) : (
                <span style={{ fontWeight: 600, color: Color.primaryBlack }}>{rowData.name}</span>
              )}
            </div>
          </div>
        );
      },
      header: () => (
        <ColumnHeader
          title={t('Title')}
          sort={sortColumn}
          sortDirection={sortDirection}
          onSort={() => handleSortingChange('title')}
          isSortable={true}
        />
      ),
    },
    {
      id: 'type',
      accessorFn: (row) => row.goalType,
      cell: ({ row }) => {
        const rowData = row.original;
        const colors = getTypePillColor(rowData.goalType);
        return (
          <TypePill style={{ backgroundColor: colors.background, color: colors.color }}>
            {getTypeDisplay(rowData.goalType)}
          </TypePill>
        );
      },
      header: () => (
        <ColumnHeader
          title={t('Type')}
          sort={sortColumn}
          sortDirection={sortDirection}
          onSort={() => handleSortingChange('type')}
          isSortable={true}
        />
      ),
    },
    {
      id: 'difficulty',
      accessorFn: (row) => row.readableDifficulty,
      cell: ({ row }) => {
        const rowData = row.original;
        const isTypeNotApplicable = rowData.goalType === 'scorm' || rowData.goalType === 'survey';
        const isNotApplicable = isTypeNotApplicable || !rowData.readableDifficulty;
        const difficulty = isNotApplicable ? 'N/A' : rowData.readableDifficulty;
        const styles = getDifficultyColors(difficulty);
        const chip = <Chip style={{ ...styles, borderRadius: '6px' }} variant="outlined" label={difficulty} />;
        if (isNotApplicable) {
          let tooltipText: string;
          if (rowData.goalType === 'scorm') {
            tooltipText = t('Difficulty does not apply to SCORM content');
          } else if (rowData.goalType === 'survey') {
            tooltipText = t('Difficulty does not apply to Surveys');
          } else {
            tooltipText = t('Not enough study data to calculate difficulty');
          }
          return <Tooltip title={tooltipText}>{chip}</Tooltip>;
        }
        return chip;
      },
      header: () => (
        <ColumnHeader
          title={t('Difficulty')}
          sort={sortColumn}
          sortDirection={sortDirection}
          onSort={() => handleSortingChange('difficulty')}
          isSortable={true}
        />
      ),
    },
    {
      id: 'shared',
      accessorFn: (row) => row.isShared,
      cell: ({ row }) => {
        const rowData = row.original;
        return <Switch checked={getSharedState(rowData)} onChange={() => toggleSetPrivacy(rowData)} color="primary" />;
      },
      header: () => (
        <TableHeaderContainer style={{ justifyContent: 'left' }}>
          <TableHeaderText>{t('Shared')}</TableHeaderText>
          <Tooltip
            title={t('Makes content available in the shared assignment picker when adding to courses')}
            placement="top"
          >
            <span style={{ marginLeft: '4px', color: Color.reportGray, cursor: 'help' }}>
              <FontAwesomeIcon icon={faQuestionCircle} />
            </span>
          </Tooltip>
        </TableHeaderContainer>
      ),
    },
    {
      id: 'used_in',
      accessorFn: (row) => row.courses?.length || 0,
      cell: ({ row }) => {
        const rowData = row.original;
        const courseCount = rowData.courses?.length || 0;
        const countText = getCourseCountText(rowData.courses);

        if (courseCount > 0) {
          return <UsedInLink onClick={() => handleUsedInClick(rowData)}>{countText}</UsedInLink>;
        }

        return <UsedInText>{countText}</UsedInText>;
      },
      header: () => (
        <ColumnHeader
          title={t('Used In')}
          sort={sortColumn}
          sortDirection={sortDirection}
          onSort={() => handleSortingChange('used_in')}
          isSortable={true}
        />
      ),
    },
    {
      id: 'last_updated',
      accessorFn: (row) => row.updatedAt,
      cell: ({ row }) => <LastUpdatedCell>{moment(row.original.updatedAt).format('MM/DD/YY')}</LastUpdatedCell>,
      header: () => (
        <ColumnHeader
          title={t('Last Updated')}
          sort={sortColumn}
          sortDirection={sortDirection}
          onSort={() => handleSortingChange('last_updated')}
          isSortable={true}
        />
      ),
    },
    {
      id: 'actions',
      header: () => (
        <TableHeaderContainer style={{ justifyContent: 'center' }}>
          <TableHeaderText>{t('Actions')}</TableHeaderText>
        </TableHeaderContainer>
      ),
      cell: ({ row }) => {
        const rowData = row.original;
        const isMenuOpen = showActionsMenu[rowData.id] || false;

        return (
          <ActionsContainer>
            <MoreVertButton
              click={() => toggleActionsMenu(rowData.id)}
              aria-controls={`content-actions-menu-${rowData.id}`}
            />
            {isMenuOpen && (
              <ActionsMenu onMouseLeave={() => toggleActionsMenu(rowData.id)} id={`content-actions-menu-${rowData.id}`}>
                {rowData.goalType !== 'scorm' && rowData.state !== 'building' && (
                  rowData.learnVersion === 4 ? (
                    <ActionLink to={getPreviewSetUrlForReactRouter(rowData.learnVersion, rowData.id)}>Preview</ActionLink>
                  ) : (
                    <ActionAnchor href={getPreviewSetUrl(rowData.learnVersion, rowData.id)}>Preview</ActionAnchor>
                  )
                )}
                {rowData.goalType !== 'scorm' && rowData.state !== 'building' && (
                  rowData.learnVersion === 4 ? (
                    <ActionLink to={getEditSetUrlForReactRouter(rowData.learnVersion, rowData.id)}>Edit</ActionLink>
                  ) : (
                    <ActionAnchor href={getEditSetUrl(rowData.learnVersion, rowData.id)}>Edit</ActionAnchor>
                  )
                )}
                {rowData.goalType !== 'survey' && (
                  <ActionButton onClick={() => handleAddToCourseClick(rowData)}>Add to course</ActionButton>
                )}
                {canCopy(rowData) && (
                  <ActionButton
                    onClick={() => {
                      toggleActionsMenu(rowData.id);
                      handleCopy(rowData, rowData.goalType, 'Copy content');
                    }}
                    disabled={copyingSetId === rowData.id}
                  >
                    {copyingSetId === rowData.id ? t('Copying...') : t('Copy')}
                  </ActionButton>
                )}
                {canCopyAsAssessment(rowData) && (
                  <ActionButton
                    onClick={() => {
                      toggleActionsMenu(rowData.id);
                      handleCopy(rowData, 'assessment', 'Copy as assessment');
                    }}
                    disabled={copyingSetId === rowData.id}
                  >
                    {copyingSetId === rowData.id ? t('Copying...') : t('Copy as Assessment')}
                  </ActionButton>
                )}
                {canCopyAsLearningSet(rowData) && (
                  <ActionButton
                    onClick={() => {
                      toggleActionsMenu(rowData.id);
                      handleCopy(rowData, 'set', 'Copy as learning set');
                    }}
                    disabled={copyingSetId === rowData.id}
                  >
                    {copyingSetId === rowData.id ? t('Copying...') : t('Copy as Learning Set')}
                  </ActionButton>
                )}
                <DeleteButton onClick={() => handleDeleteClick(rowData)}>Delete</DeleteButton>
              </ActionsMenu>
            )}
          </ActionsContainer>
        );
      },
    },
  ];

  const handleSortingChange = (sortBy: string) => {
    if (sortBy === sortColumn) {
      toggleSortDirection();
    } else {
      setSortColumn(sortBy);
      setSortDirection('asc');
    }
    // Reset to page 1 when sorting changes
    setPageNumber(1);
  };

  const table = useReactTable({
    data: sets,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: { rowSelection },
    onRowSelectionChange: setRowSelection,
    enableRowSelection: true,
    getRowId: (row) => row.id,
  });

  if (queryStatus === 'loading') {
    return <LoadingComponent component={<></>} isLoading={true} />;
  }

  const tableContent =
    queryStatus === 'error' ? (
      errorMessage
    ) : (
      <>
        <StyledTable>
          <TableHeader table={table} handleHeaderClick={() => {}} />
          <TableBody table={table} />
        </StyledTable>
        {sets.length === 0 && totalCount === 0 && noSetsMessage}
        {totalCount > 0 && (
          <Pagination
            totalCount={totalCount}
            page={pageNumber}
            pageSize={pageSize}
            goToPageOne={goToPageOne}
            goToPreviousPage={goToPreviousPage}
            goToNextPage={goToNextPage}
            goToLastPage={goToLastPage}
            onPageSizeChange={handlePageSizeChange}
          />
        )}
      </>
    );

  return (
    <div>
      <SearchAndFilterContainer>
        <SearchContainer>
          <TableSearchBar
            value={searchName}
            onChange={setSearchName}
            resetPage={() => setPageNumber(1)}
            placeholder={t('Search content')}
          />
        </SearchContainer>
        <ContentTableFilters filters={filters} onFiltersChange={setFilters} getTypeDisplay={getTypeDisplay} />
      </SearchAndFilterContainer>
      <ContentTableFilterChips
        filters={filters}
        onFiltersChange={setFilters}
        getTypeDisplay={getTypeDisplay}
        isLoading={isFetching}
      />
      {selectedCount > 0 && (
        <BulkActionBar>
          <SelectionCount>{pluralize(selectedCount, 'assignment')} selected</SelectionCount>
          <Tooltip
            title={hasOnlySurveys ? t('Surveys must be added to courses from the course detail page') : ''}
            placement="top"
          >
            <span>
              <PrimaryButton click={handleBulkAddToCourse} disabled={hasOnlySurveys}>
                {t('Add to Course')}
              </PrimaryButton>
            </span>
          </Tooltip>
          <PrimaryButton click={handleBulkDelete} destructive>
            {t('Delete')}
          </PrimaryButton>
          <DeselectButton onClick={() => setRowSelection({})}>{t('Deselect All')}</DeselectButton>
        </BulkActionBar>
      )}
      <TableWrapper>
        {isFetching && (
          <LoadingSpinner>
            <CircularProgress size={20} />
          </LoadingSpinner>
        )}
        <TableContainer>{tableContent}</TableContainer>
      </TableWrapper>

      {setsToDelete.length > 0 && (
        <DeleteConfirmationModal
          title={
            setsToDelete.length === 1
              ? `Are you sure you want to delete "${setsToDelete[0].name}"?`
              : `Are you sure you want to delete ${pluralize(setsToDelete.length, 'assignment')}?`
          }
          body={getDeleteConfirmationBody(setsToDelete)}
          requireTextConfirmation={true}
          isDeleting={isDeleting}
          onCancel={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
        />
      )}

      {showUsedInModal && selectedSetForUsedIn && (
        <ContentUsageModal contentItem={selectedSetForUsedIn} onClose={handleUsedInModalClose} />
      )}

      {showAddToCourseModal && selectedSetForAddToCourse && (
        <AddToCourseModal
          contentItems={selectedSetForAddToCourse}
          excludedSurveyCount={excludedSurveyCountForModal}
          onClose={handleAddToCourseModalClose}
        />
      )}

      {isCopying && <LoadingModal message={t('Copying content...')} />}
    </div>
  );
};

interface ColumnHeaderProps {
  title: string;
  sort: string;
  sortDirection: 'asc' | 'desc';
  onSort: () => void;
  isSortable?: boolean;
}

const ColumnHeader = ({ title, sort, sortDirection, onSort, isSortable = false }: ColumnHeaderProps) => {
  return (
    <TableHeaderContainer
      style={Object.assign({ justifyContent: 'left', whiteSpace: 'nowrap' }, isSortable ? { cursor: 'pointer' } : {})}
      onClick={isSortable ? onSort : undefined}
    >
      <TableHeaderText>{title}</TableHeaderText>
      {sort === formatStringForSortComparison(title) && (
        <SortArrow icon={sortDirection === 'desc' ? faArrowDown : faArrowUp} />
      )}
    </TableHeaderContainer>
  );
};

const SContentImageContainer = styled.div`
  display: inline-block;
`;

const contentImageStyles = {
  height: '32px',
  width: '32px',
  verticalAlign: 'middle',
  marginRight: '25px',
};

const TableWrapper = styled.div`
  position: relative;
`;

const LoadingSpinner = styled.div`
  position: absolute;
  top: 12px;
  left: 8px;
  z-index: 5;
`;

const TableContainer = styled.div``;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TypePill = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  text-align: center;
  white-space: nowrap;
`;

const UsedInLink = styled.button`
  background: none;
  border: none;
  color: ${Color.primaryBlue};
  cursor: pointer;
  text-decoration: underline;
  font-size: inherit;
  font-family: inherit;
  padding: 0;
  white-space: nowrap;

  &:hover {
    text-decoration: none;
  }

  &:focus {
    outline: 2px solid ${Color.primaryBlue};
    outline-offset: 2px;
  }
`;

const UsedInText = styled.span`
  white-space: nowrap;
`;

const LastUpdatedCell = styled.span`
  white-space: nowrap;
`;

const ActionsContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
`;

const ActionsMenu = styled.div`
  position: absolute;
  top: 40px;
  right: 0;
  background: white;
  border: 1px solid ${Color.lightGray};
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 10;
  padding: 4px;
  white-space: nowrap;

  > * {
    margin: 4px 0;
  }
`;

const baseActionStyles = `
  background: transparent;
  border: none;
  color: ${Color.darkGray};
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  font-size: 16px;
  text-decoration: none;
  text-align: left;
  width: 100%;
  box-sizing: border-box;

  &:hover {
    background-color: ${Color.primaryGray};
    text-decoration: none;
  }
`;

const ActionLink = styled(Link)`
  ${baseActionStyles}
`;

const ActionAnchor = styled.a`
  ${baseActionStyles}
`;

const ActionButton = styled.button`
  ${baseActionStyles}
`;

const DeleteButton = styled(ActionButton)`
  color: ${Color.red};
`;

const SearchAndFilterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 0;
  border-bottom: 1px solid ${Color.lightGray};
`;

const SearchContainer = styled.div`
  flex: 1;
`;

const titleLinkStyles = `
  font-weight: 600;
  color: ${Color.primaryBlack};
  text-decoration: none;

  &:hover {
    text-decoration: underline !important;
    color: ${Color.primaryBlue} !important;
  }

  &:focus {
    outline: 2px solid ${Color.primaryBlue};
    outline-offset: 2px;
  }
`;

const TitleLink = styled(Link)`
  ${titleLinkStyles}
`;

const TitleAnchor = styled.a`
  ${titleLinkStyles}
`;

